import {useEffect, useState} from "react";
import {
    GetExecutiveSummaryNarrativeQuery,
    useCreateExecutiveSummaryNarrativeSectionMutation,
    useDeleteExecutiveSummaryNarrativeSectionMutation,
    useGetExecutiveSummaryNarrativeQuery,
    useMoveExecutiveSummaryNarrativeSectionMutation,
    useSetExecutiveSummaryNarrativeUseCustomNarrativeMutation,
    useUpdateExecutiveSummaryNarrativeSectionMutation,
} from "../../../../__generated__/generated_types";
import {Property} from "../../../../contexts/properties/PropertiesContext";


export type NarrativeSection = NonNullable<GetExecutiveSummaryNarrativeQuery["getExecutiveSummaryNarrative"]>["sections"][number];

interface UseExecutiveSummaryNarrative {
    sections: NarrativeSection[];
    useCustomNarrative: boolean;
    isLoading: boolean;
    createSection: (title: string, body: string) => Promise<boolean>;
    updateSection: (sectionId: string, title: string, body: string) => Promise<boolean>;
    deleteSection: (sectionId: string) => Promise<boolean>;
    moveSection: (sectionId: string, previousSectionId: string | null) => Promise<boolean>;
    setUseCustomNarrative: (useCustomNarrative: boolean) => Promise<boolean>;
}

export default function useExecutiveSummaryNarrative(property: Property): UseExecutiveSummaryNarrative {
    const [sections, setSections] = useState<NarrativeSection[]>([]);
    const [useCustomNarrative, setUseCustomNarrativeState] = useState(false);

    const {data, loading} = useGetExecutiveSummaryNarrativeQuery({
        variables: {
            propertyId: property.id,
            budgetYear: property.budgetYear,
        },
        fetchPolicy: "no-cache",
    });
    const [createSectionMutation] = useCreateExecutiveSummaryNarrativeSectionMutation();
    const [updateSectionMutation] = useUpdateExecutiveSummaryNarrativeSectionMutation();
    const [deleteSectionMutation] = useDeleteExecutiveSummaryNarrativeSectionMutation();
    const [moveSectionMutation] = useMoveExecutiveSummaryNarrativeSectionMutation();
    const [setUseCustomNarrativeMutation] = useSetExecutiveSummaryNarrativeUseCustomNarrativeMutation();

    useEffect(() => {
        setSections(data?.getExecutiveSummaryNarrative?.sections ?? []);
        setUseCustomNarrativeState(data?.getExecutiveSummaryNarrative?.useCustomNarrative ?? false);
    }, [data]);

    async function createSection(title: string, body: string): Promise<boolean> {
        try {
            const result = await createSectionMutation({
                variables: {
                    propertyId: property.id,
                    budgetYear: property.budgetYear,
                    title: title,
                    body: body,
                },
            });
            const newSectionId = result.data?.createExecutiveSummaryNarrativeSection;
            if (!newSectionId) {
                return false;
            }

            setSections(currentSections => [
                ...currentSections,
                {
                    id: newSectionId,
                    title: title.trim(),
                    body: body.trim(),
                },
            ]);
            return true;
        } catch {
            return false;
        }
    }

    async function updateSection(sectionId: string, title: string, body: string): Promise<boolean> {
        try {
            await updateSectionMutation({
                variables: {
                    sectionId: sectionId,
                    title: title,
                    body: body,
                },
            });
            setSections(currentSections => currentSections.map(section => {
                if (section.id !== sectionId) {
                    return section;
                }

                return {
                    ...section,
                    title: title.trim(),
                    body: body.trim(),
                };
            }));
            return true;
        } catch {
            return false;
        }
    }

    async function deleteSection(sectionId: string): Promise<boolean> {
        try {
            await deleteSectionMutation({
                variables: {
                    sectionId: sectionId,
                },
            });
            setSections(currentSections => currentSections.filter(section => section.id !== sectionId));
            return true;
        } catch {
            return false;
        }
    }

    async function moveSection(sectionId: string, previousSectionId: string | null): Promise<boolean> {
        const previousSections = sections;
        const movedSection = sections.find(section => section.id === sectionId);
        if (!movedSection) {
            return false;
        }

        const reorderedSections = sections.filter(section => section.id !== sectionId);
        let insertIndex = 0;
        if (previousSectionId !== null) {
            insertIndex = reorderedSections.findIndex(section => section.id === previousSectionId) + 1;
        }
        reorderedSections.splice(insertIndex, 0, movedSection);
        setSections(reorderedSections);

        try {
            await moveSectionMutation({
                variables: {
                    sectionId: sectionId,
                    previousSectionId: previousSectionId,
                },
            });
            return true;
        } catch {
            setSections(previousSections);
            return false;
        }
    }

    async function setUseCustomNarrative(newUseCustomNarrative: boolean): Promise<boolean> {
        const previousUseCustomNarrative = useCustomNarrative;
        setUseCustomNarrativeState(newUseCustomNarrative);

        try {
            await setUseCustomNarrativeMutation({
                variables: {
                    propertyId: property.id,
                    budgetYear: property.budgetYear,
                    useCustomNarrative: newUseCustomNarrative,
                },
            });
            return true;
        } catch {
            setUseCustomNarrativeState(previousUseCustomNarrative);
            return false;
        }
    }

    return {
        sections: sections,
        useCustomNarrative: useCustomNarrative,
        isLoading: loading,
        createSection: createSection,
        updateSection: updateSection,
        deleteSection: deleteSection,
        moveSection: moveSection,
        setUseCustomNarrative: setUseCustomNarrative,
    };
}
